import React, { useEffect } from 'react';
import { Button, List, Skeleton } from 'antd';
import dayjs from 'common/config/dayjs.config';
import { ReactComponent as ReferralGiftIcon } from 'app/assets/images/referralGift.svg';
import { ReactComponent as BitecoinIcon } from 'app/assets/images/bitecoinIcon.svg';
import { ReactComponent as ShopBagIcon } from 'app/assets/images/shopBag.svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { clearReferralHistory, getReferralHistory } from 'app/store/reducers/referral.reducer';
import { EReferralHistoryType, IReferralHistoryItemModel } from 'entities/Referral/Referral.models';

const PAGE_SIZE = 10;
// Figma 14513:16991: "March 29, 2021 at 11:27 AM"
const HISTORY_DATE_FORMAT = 'MMMM D, YYYY [at] h:mm A';

const buildItemTitle = (type: EReferralHistoryType): string => {
  switch (type) {
    case EReferralHistoryType.Referral:
      return 'Someone used your referral code!';
    case EReferralHistoryType.Order:
      return 'You earned Bitecoins from your order';
    case EReferralHistoryType.Spent:
      return 'You spent Bitecoins on your order';
    case EReferralHistoryType.Refund:
      return 'Bitecoins refunded from your cancelled order';
    case EReferralHistoryType.Expired:
      return 'Your Bitecoins expired';
    default:
      // Unknown future ledger type — render generically instead of crashing.
      return 'Bitecoins';
  }
};

const isSpendingType = (type: EReferralHistoryType): boolean =>
  type === EReferralHistoryType.Spent || type === EReferralHistoryType.Expired;

const renderItem = (item: IReferralHistoryItemModel, index: number) => {
  const date = dayjs(item.date).format(HISTORY_DATE_FORMAT);
  // bitecoinsAmount comes positive from the api; direction is carried by `type`.
  const amount = Math.abs(item.bitecoinsAmount);
  const isSpending = isSpendingType(item.type);
  const isOrderType = item.type === EReferralHistoryType.Order;

  return (
    <List.Item key={`${item.type}-${item.date}-${index}`} className="referral-history__item">
      <div className="referral-history__icon">{isSpending || isOrderType ? <ShopBagIcon /> : <ReferralGiftIcon />}</div>
      <div className="referral-history__body">
        <div className="referral-history__item-title">{buildItemTitle(item.type)}</div>
        <div className="referral-history__date">{date}</div>
      </div>
      <div className={`referral-history__amount${isSpending ? ' referral-history__amount_spending' : ''}`}>
        <span>{`${isSpending ? '−' : '+'}${amount}`}</span>
        <BitecoinIcon />
      </div>
    </List.Item>
  );
};

export const ReferralHistoryList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { history, historyCount, historyLoading } = useAppSelector((state) => state.referral);

  useEffect(() => {
    dispatch(clearReferralHistory());
    dispatch(getReferralHistory({ offset: 0, limit: PAGE_SIZE }, false));
  }, []);

  const loadMore = () => {
    dispatch(getReferralHistory({ offset: history.length, limit: PAGE_SIZE }, true));
  };

  const hasMore = history.length < historyCount;

  return (
    <div className="referral-history">
      {historyLoading && history.length === 0 ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <>
          <List dataSource={history} locale={{ emptyText: 'No Bitecoins activity yet.' }} renderItem={renderItem} />
          {hasMore && (
            <Button block loading={historyLoading} onClick={loadMore} className="mt-4">
              Load more
            </Button>
          )}
        </>
      )}
    </div>
  );
};
